import { Slide } from "@jelly/lesson";
import config from "appConfig";

const principle: Slide = {
  title: "Sound reduction index",
  layout: {
    type: "customCase010203",
    options: {
      page1: {
        title: "Sound reduction index of a panel: Definition and norm ISO 15186-1:2000 (EN)",
        list: [
          `The sound reduction index of a panel used as a building element is a measure of the sound transmission loss across the panel.`,
          `The norm ISO 15186, provided by the International Organization for Standardization (ISO) and entitled Acoustics - Measurement of sound insulation in buildings and of building elements using sound intensity, “specifies a sound intensity method to determine the sound reduction index and the element-normalized level difference of building elements”. Its part 1 is dedicated to laboratory measurements and gives details for determining this index in a laboratory.`,
          `The transmission loss is defined, in dB, as
          <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
            <mi>T</mi>
            <mi>L</mi>
            <mo>=</mo>
            <mo>-</mo>
            <mn>10</mn>
            <mi>l</mi>
            <mi>o</mi>
            <msub>
              <mi>g</mi>
              <mn>10</mn>
            </msub>
            <mo>(</mo>
            <mi>T</mi>
            <mo>)</mo>
          </math>
          , where
          <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
            <mi>T</mi>
          </math>
          is the transmission of the sound energy:
          <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
            <mi>T</mi>
            <mo>=</mo>
            <mfrac>
              <msub>
                <mi scriptlevel=0>W</mi>
                <mi scriptlevel="+1">t</mi>
              </msub>
              <msub>
                <mi scriptlevel=0>W</mi>
                <mi scriptlevel="+1">i</mi>
              </msub>
            </mfrac>
            <mo>=</mo>
            <mfrac>
              <msub>
                <mi scriptlevel=0>I</mi>
                <mi scriptlevel="+1">t</mi>
              </msub>
              <msub>
                <mi scriptlevel=0>I</mi>
                <mi scriptlevel="+1">i</mi>
              </msub>
            </mfrac>
          </math>
          (see Lesson 1.9 for details).`,
          `The transmission of the sound energy is hence the ratio of the transmitted to the incident power (
          <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
            <mi>W</mi>
          </math>
          ) or intensity (
          <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
            <mi>I</mi>
          </math>
          ).`,
        ],
        image: `${config.ASSETS_URL}/01/case-02/slide03-image01.svg`,
      },
      page2: {
        title: "Sound reduction index of a panel: Definition and norm ISO 15186-1:2000 (EN)",
        list: [
          `To get the sound reduction index, the aim is to measure the intrinsic transmission loss of the panel. To do so, sources are placed in a room and produce the incident wave.The pressure level is measured in different points and an average
          <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
            <msub>
              <mi>p</mi>
              <mi>i</mi>
            </msub>
          </math>
          is taken.
          Assuming a diffuse field (uniform pressure in the room), we have
          <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
            <msub>
              <mi>I</mi>
              <mi>i</mi>
            </msub>
            <mo>=</mo>
            <mfrac>
              <mrow>
                <msubsup>
                  <mi scriptlevel=0>p</mi>
                  <mi scriptlevel="+1">i</mi>
                  <mn scriptlevel="+1">2</mn>
                </msubsup>
              </mrow>
              <mrow>
                <mn scriptlevel=0>4</mn>
                <msub>
                  <mi scriptlevel=0>ρ</mi>
                  <mi scriptlevel="+1">0</mi>
                </msub>
                <mi scriptlevel=0>c</mi>
              </mrow>
            </mfrac>
          </math>
          .`,
          `The transmitted normal intensity is measured in the vicinity of the panel on a surface Σ usually lower than the panel surface S, at different positions, and the measured intensities are averaged to get
          <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
            <msub>
              <mi>I</mi>
              <mi>t</mi>
            </msub>
          </math>
          .`,
          `The sound reduction index is then given by
          <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
            <mi>R</mi>
            <mo>=</mo>
            <mo>-</mo>
            <mn>10</mn>
            <mi>l</mi>
            <mi>o</mi>
            <msub>
              <mi>g</mi>
              <mn>10</mn>
            </msub>
            <mo>(</mo>
            <mi>T</mi>
            <mo>)</mo>
          </math>
          , with
          <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>

            <mi>T</mi>
            <mo>=</mo>
            <mfrac>
              <msub>
                <mi scriptlevel=0>I</mi>
                <mi scriptlevel="+1">t</mi>
              </msub>
              <msub>
                <mi scriptlevel=0>I</mi>
                <mi scriptlevel="+1">i</mi>
              </msub>
            </mfrac>
            <mo>=</mo>
            <mfrac>
              <msub>
                <mi scriptlevel=0>I</mi>
                <mi scriptlevel="+1">t</mi>
              </msub>
              <mrow>
                <mfrac>
                  <mrow>
                    <msubsup>
                      <mi scriptlevel=0>p</mi>
                      <mi scriptlevel="+1">i</mi>
                      <mn scriptlevel="+1">2</mn>
                    </msubsup>
                  </mrow>
                  <mrow>
                    <mn scriptlevel=0>4</mn>
                    <msub>
                      <mi scriptlevel=0>ρ</mi>
                      <mi scriptlevel="+1">0</mi>
                    </msub>
                    <mi scriptlevel=0>c</mi>
                  </mrow>
                </mfrac>
              </mrow>
            </mfrac>
          </math>
          .<br />Note that the sound reduction index R is thus a specific case of a transmission loss TL where the incident field is a diffuse field.`,
        ],
        image: `${config.ASSETS_URL}/01/case-02/slide03-image02.svg`,
      },
    },
  },
};

export default principle;
